import { useState, useRef, useEffect, useCallback } from "react";
import {
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import RecommendationProductCard from "../RecommendationProductCard";
import {
    getRecommendationProducts,
    RecommendationProduct,
} from "../RecommendationProductsData";
import "./RelatedItemsSection.css";

type RelatedItemsSectionProps = {
    category?: string;
    products?: any[];
};

const RelatedItemsSection = ({
    category,
    products: dynamicProducts,
}: RelatedItemsSectionProps) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const mappedProducts: RecommendationProduct[] = (dynamicProducts || []).map((p: any) => {
        const rawPrice = Number(p.featuredOffer?.finalPrice ?? p.featuredOffer?.unitPrice ?? p.price ?? 0);
        const rawMrp = Number(p.featuredOffer?.mrp ?? p.mrp ?? rawPrice);
        const finalPrice = rawPrice > 0 ? rawPrice : rawMrp;
        const finalMrp = rawMrp >= finalPrice ? rawMrp : finalPrice;
        const deal = p.featuredOffer?.deal?.dealName;
        const desc = p.shortDescription || p.description;

        return {
            id: p._id || p.ssin,
            badge: deal && deal !== "-" ? deal : "RECOMMENDED",
            title: p.name || "-",
            description: desc && desc !== "-" ? desc : "",
            image: p.mainImage || (Array.isArray(p.images) ? p.images[0] : ""),
            colors: [],
            extraColors: 0,
            rating: Number(p.reviews?.averageRating ?? 0),
            reviews: p.reviews?.totalReviews ? String(p.reviews.totalReviews) : "0",
            deal: deal && deal !== "-" ? deal : "",
            dealType: "limited",
            price: finalPrice,
            mrp: finalMrp,
            delivery: p.featuredOffer?.delivery?.freeDelivery
                ? "Free Delivery"
                : p.fastDelivery
                ? "Fast Delivery"
                : "Free Delivery",
            deliveryType: "delivery",
            category: p.categoryHierarchy?.superCategory || category || "-",
        };
    });

    const products =
        mappedProducts.length > 0
            ? mappedProducts
            : getRecommendationProducts(
                category,
                "related"
            );

    const updateScrollButtons = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setCanScrollLeft(scrollLeft > 10);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateScrollButtons();
        el.addEventListener("scroll", updateScrollButtons, { passive: true });
        window.addEventListener("resize", updateScrollButtons);
        return () => {
            el.removeEventListener("scroll", updateScrollButtons);
            window.removeEventListener("resize", updateScrollButtons);
        };
    }, [products, updateScrollButtons]);

    const handleScroll = (direction: "left" | "right") => {
        const el = scrollRef.current;
        if (!el) return;
        const scrollAmount = Math.max(el.clientWidth * 0.75, 250);
        el.scrollBy({
            left: direction === "left" ? -scrollAmount : scrollAmount,
            behavior: "smooth",
        });
    };

    if (products.length === 0) {
        return null;
    }

    return (
        <section className="related-items">
            <div className="related-items-container">
                <div className="related-items-header">
                    <div>
                        <span>
                            HANDPICKED FOR YOU
                        </span>

                        <h2>
                            More items related to this product
                        </h2>

                        <p>
                            Similar products selected around the item you're currently viewing.
                        </p>
                    </div>

                    <div className="related-items-header-actions">
                        <div className="related-items-header-nav">
                            <button
                                type="button"
                                className="related-items-header-btn"
                                onClick={() => handleScroll("left")}
                                disabled={!canScrollLeft}
                                aria-label="Previous items"
                                title="Previous items"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                type="button"
                                className="related-items-header-btn"
                                onClick={() => handleScroll("right")}
                                disabled={!canScrollRight}
                                aria-label="Next items"
                                title="Next items"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>

                        <button type="button" className="related-items-explore-btn">
                            Explore all
                            <ChevronRight
                                size={16}
                            />
                        </button>
                    </div>
                </div>

                <div className="related-items-carousel-wrapper">
                    {canScrollLeft && (
                        <button
                            type="button"
                            className="related-items-nav-btn prev"
                            onClick={() => handleScroll("left")}
                            aria-label="Scroll previous"
                            title="Scroll previous"
                        >
                            <ChevronLeft size={20} />
                        </button>
                    )}

                    <div
                        className="related-items-scroll-track"
                        ref={scrollRef}
                    >
                        {products.map(
                            (
                                product
                            ) => (
                                <RecommendationProductCard
                                    key={
                                        product.id
                                    }
                                    product={
                                        product
                                    }
                                />
                            )
                        )}
                    </div>

                    {canScrollRight && (
                        <button
                            type="button"
                            className="related-items-nav-btn next"
                            onClick={() => handleScroll("right")}
                            aria-label="Scroll next"
                            title="Scroll next"
                        >
                            <ChevronRight size={20} />
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
};

export default RelatedItemsSection;